import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Fade,
  LinearProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  Checkbox,
  FormControlLabel,
  Grid,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  Divider,
  ListSubheader
} from '@mui/material';
import {
  AutoAwesome,
  Send,
  CheckCircle,
  ArrowBack,
  VerifiedUser,
  Refresh,
  AttachFile,
  Close,
  Star,
  LocationOn,
  Image,
  Delete,
  FolderOpen,
  Assignment,
  Shield
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { brand, border, bg } from '../../theme/colors';
import VerifierHireDocumentPreview from './VerifierHireDocumentPreview';
import DialogCloseButton from '../common/DialogCloseButton';

const TOTAL_STEPS = 4;

// Standard Zimbabwe construction inspection/audit stages (verifiers verify these)
const CONSTRUCTION_STAGES = [
  {
    id: 'building_plans',
    name: 'Building Plans',
    purpose: 'Preparation and approval of architectural drawings by the Local Authority.',
    auditFocus: 'Plan compliance, Local Authority submission, regulatory requirements.',
    suggestion: 'Requesting verification quotes for the review and approval of building plans submitted to the Local Authority. This includes checking architectural drawings for compliance with local building regulations, verifying submission completeness, and confirming adherence to regulatory requirements.'
  },
  {
    id: 'siting_setting_out',
    name: 'Siting and Setting Out',
    purpose: 'Verify the building is correctly positioned on the stand per approved site plans and boundary lines.',
    auditFocus: 'Boundary checks, building line compliance.',
    suggestion: 'Requesting verification quotes for siting and setting-out inspection. Scope includes verifying the building is correctly positioned on the stand (plot) per approved site plans, conducting boundary checks, confirming building line compliance, and validating the foundation layout pegging.'
  },
  {
    id: 'trenches_foundation',
    name: 'Trenches / Foundation Level',
    purpose: 'Inspect depth, width, and stability of trenches before concrete is poured.',
    auditFocus: 'Reinforcing steel inspection and excavation depth to meet structural requirements.',
    suggestion: 'Requesting verification quotes for trench and foundation-level inspection. Scope includes verifying excavation depth and width meet structural specifications, inspecting reinforcing steel placement, and confirming all dimensions comply with the structural engineer\'s requirements before concrete is poured.'
  },
  {
    id: 'concrete_substructure',
    name: 'Concrete Inspection (Substructure)',
    purpose: 'Inspection of foundation concrete and anti-termite treatment.',
    auditFocus: 'Quality and depth of the concrete base.',
    suggestion: 'Requesting verification quotes for substructure concrete inspection. Scope includes verifying concrete mix quality, checking pouring depth and compaction, and confirming anti-termite treatment has been properly applied as required by approved plans.'
  },
  {
    id: 'dpc_floor_slab',
    name: 'Damp Proof Course (DPC) and Floor Slab',
    purpose: 'Inspection of DPC and oversite concrete (floor slab) to prevent moisture ingress.',
    auditFocus: 'Proper placement of DPC, plumbing pipe installation, and compacted filling.',
    suggestion: 'Requesting verification quotes for DPC and floor slab inspection. Scope includes verifying proper placement of the DPC membrane, inspecting plumbing pipe installation below the slab, checking compacted filling, and confirming the oversite concrete meets the specified thickness and mix requirements.'
  },
  {
    id: 'walling',
    name: 'Walling (Mid-construction)',
    purpose: 'Check wall construction at critical heights — bottom window level and top window level.',
    auditFocus: 'Wall thickness, structural integrity, and lintel installation.',
    suggestion: 'Requesting verification quotes for mid-construction walling inspection. Scope includes verifying wall thickness and structural integrity from DPC to wall plate level, inspecting lintel installation at window and door openings, and confirming compliance with approved plans.'
  },
  {
    id: 'roof_structure',
    name: 'Roof Structure (Roof Carcass)',
    purpose: 'Inspection of roof trusses and timber framework before covering.',
    auditFocus: 'Bracing, truss anchoring, and adherence to structural design.',
    suggestion: 'Requesting verification quotes for roof structure inspection. Scope includes verifying truss fabrication and installation, inspecting bracing and anchoring to wall plates, and confirming the timber framework complies with the approved structural design before sheeting or tiling.'
  },
  {
    id: 'drainage_sewerage',
    name: 'Drainage and Sewerage (Drain Test)',
    purpose: 'Test plumbing and drainage systems for leaks and proper flow before cover-up.',
    auditFocus: 'Pipe gradient, joint sealing, and pressure testing (drain open test).',
    suggestion: 'Requesting verification quotes for drainage and sewerage inspection. Scope includes verifying pipe gradients, inspecting joint sealing, confirming municipal sewer or septic connection compliance, and conducting a drain open test to confirm no leaks before trenches are backfilled.'
  },
  {
    id: 'final_inspection',
    name: 'Final Inspection (Occupancy Certificate)',
    purpose: 'Comprehensive inspection on completion to obtain an Occupancy Certificate.',
    auditFocus: 'Verification that all previous stages were approved, the house is safe for habitation, and it matches the approved drawings.',
    suggestion: 'Requesting verification quotes for final inspection and Occupancy Certificate assessment. Scope includes comprehensive verification that all construction stages meet approved standards, the building is safe for habitation, and the completed structure matches the approved drawings in all respects.'
  }
];

// Verification services grouped by professional and general categories
const VERIFICATION_SERVICES = [
  // Professional Verifier Services
  {
    id: 'structural_engineering',
    name: 'Structural Engineering Inspection',
    category: 'Professional',
    suggestion: 'Requesting verification quotes for a structural engineering inspection. Scope includes assessment of load-bearing elements, structural integrity analysis, compliance with structural design specifications, and a detailed written report on findings and recommendations.'
  },
  {
    id: 'architectural_plan_review',
    name: 'Architectural Plan Review & Compliance',
    category: 'Professional',
    suggestion: 'Requesting verification quotes for architectural plan review and compliance assessment. Scope includes review of architectural drawings against Local Authority requirements, building regulations compliance check, and a compliance report with any required amendments.'
  },
  {
    id: 'geotechnical_assessment',
    name: 'Geotechnical Assessment',
    category: 'Professional',
    suggestion: 'Requesting verification quotes for a geotechnical assessment. Scope includes soil testing, bearing capacity analysis, groundwater evaluation, and recommendations for foundation design suitable for the site conditions.'
  },
  {
    id: 'electrical_inspection',
    name: 'Electrical Systems Inspection',
    category: 'Professional',
    suggestion: 'Requesting verification quotes for electrical systems inspection. Scope includes wiring compliance check, load calculations verification, earthing and bonding inspection, distribution board assessment, and issuance of a compliance certificate.'
  },
  {
    id: 'plumbing_drainage',
    name: 'Plumbing & Drainage Certification',
    category: 'Professional',
    suggestion: 'Requesting verification quotes for plumbing and drainage certification. Scope includes pipe installation inspection, pressure testing, drainage gradient verification, and certification of compliance with plumbing codes.'
  },
  {
    id: 'fire_safety',
    name: 'Fire Safety Compliance',
    category: 'Professional',
    suggestion: 'Requesting verification quotes for fire safety compliance assessment. Scope includes review of fire escape routes, fire detection and suppression systems, fire-resistant material verification, and issuance of a fire safety compliance report.'
  },
  {
    id: 'environmental_impact',
    name: 'Environmental Impact Assessment',
    category: 'Professional',
    suggestion: 'Requesting verification quotes for an environmental impact assessment. Scope includes evaluation of the project\'s environmental footprint, noise and dust impact analysis, waste management plan review, and recommendations for mitigation measures.'
  },
  {
    id: 'quantity_surveying',
    name: 'Quantity Surveying & Cost Verification',
    category: 'Professional',
    suggestion: 'Requesting verification quotes for quantity surveying and cost verification. Scope includes bill of quantities review, material cost validation, labour rate assessment, and a detailed cost verification report comparing quoted versus market rates.'
  },

  // General Verifier Services
  {
    id: 'progress_monitoring',
    name: 'Construction Progress Monitoring',
    category: 'General',
    suggestion: 'Requesting verification quotes for construction progress monitoring. Scope includes periodic site visits, progress assessment against project schedule, photographic documentation, and written progress reports with any identified delays or concerns.'
  },
  {
    id: 'site_documentation',
    name: 'Photo/Video Site Documentation',
    category: 'General',
    suggestion: 'Requesting verification quotes for professional photo and video site documentation. Scope includes comprehensive visual recording of current site conditions, annotated photographs of key construction elements, and a documented visual record for project stakeholders.'
  },
  {
    id: 'material_quality',
    name: 'Material Quality Verification',
    category: 'General',
    suggestion: 'Requesting verification quotes for material quality verification. Scope includes inspection of delivered materials against specifications, verification of material grades and certifications, assessment of storage conditions, and a quality report with pass/fail status for each material.'
  },
  {
    id: 'site_safety',
    name: 'Site Safety Audit',
    category: 'General',
    suggestion: 'Requesting verification quotes for a site safety audit. Scope includes assessment of safety practices, PPE compliance, hazard identification, scaffolding and temporary works inspection, and a safety audit report with corrective actions required.'
  },
  {
    id: 'workmanship_quality',
    name: 'Workmanship Quality Check',
    category: 'General',
    suggestion: 'Requesting verification quotes for a workmanship quality check. Scope includes inspection of construction quality against standards, identification of defects or non-conformances, and a detailed quality assessment report with recommended remedial actions.'
  },
  {
    id: 'measurement_survey',
    name: 'Measurement & Survey Verification',
    category: 'General',
    suggestion: 'Requesting verification quotes for measurement and survey verification. Scope includes independent measurement of completed works, verification against approved drawings and dimensions, and a measurement report confirming or noting discrepancies.'
  },
  {
    id: 'milestone_verification',
    name: 'Completion Milestone Verification',
    category: 'General',
    suggestion: 'Requesting verification quotes for completion milestone verification. Scope includes inspection of works claimed as complete against the milestone scope, quality assessment, photographic evidence, and a written verification report confirming milestone completion status.'
  },

  // Custom
  {
    id: 'other',
    name: 'Other \u2014 Write your own',
    category: 'Custom',
    suggestion: ''
  }
];

// Label map used in the "Your Answers" summary panel
const ANSWER_LABELS = {
  workType: 'Verification scope',
  location: 'Project location',
  timeline: 'Timeline',
  description: 'Verification details'
};

/**
 * Build a verification-oriented scope description from a project's details.
 * The project description is written from the contractor's perspective
 * (e.g. "I require a professional painting service…"). This helper reframes
 * it as a verification scope so verifiers understand what needs to be inspected.
 */
const buildVerificationScopeFromProject = (project) => {
  if (!project) return '';
  const title = project.title || 'the project';
  const location = project.location ? ` at ${project.location}` : '';
  const desc = project.description
    ? `\n\nProject overview: ${project.description}\n\nPlease verify that the contractor's work meets the agreed specifications, relevant building codes, and quality standards.`
    : '';
  return `Requesting verification quotes for the entire scope of "${title}"${location}.${desc}`;
};

/**
 * AIVerifierQuoteDialog
 * 5-step conversational dialog for sending quote requests to verifiers.
 *
 * Step 0 — Project Association: pick an existing project (auto-selects if only one)
 * Step 1 — Scope Selection: entire project / construction stage / verification service
 * Step 2 — Timeline
 * Step 3 — Description
 *
 * Props:
 *   open        — boolean
 *   onClose     — callback
 *   onComplete  — callback({ verifiers, answers })
 *   verifierId  — optional; when set the verifier selection step is skipped
 *                  and the request goes directly to that verifier.
 */
const AIVerifierQuoteDialog = ({ open, onClose, onComplete, verifierId = null }) => {
  const { getApiClient, user } = useAuth();

  // ── Wizard navigation ──────────────────────────────────────────────────────
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [improving, setImproving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // ── Verifier selection (post-questions) ──────────────────────────────────
  const [matchedVerifiers, setMatchedVerifiers] = useState([]);
  const [selectedVerifiers, setSelectedVerifiers] = useState([]);
  const [showVerifierSelection, setShowVerifierSelection] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // ── Filter state for verifier selection grid ─────────────────────────────
  const [filterSearch, setFilterSearch] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [showPreviousOnly, setShowPreviousOnly] = useState(false);
  const [previousVerifiers, setPreviousVerifiers] = useState([]);

  // ── Step 0: Project Association ────────────────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // ── Step 1: Scope Selection ────────────────────────────────────────────────
  const [scopeType, setScopeType] = useState('entire'); // 'entire' | 'stage' | 'service'
  const [scopeDescription, setScopeDescription] = useState('');
  const [selectedStageId, setSelectedStageId] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState('');

  // ── Questions for steps 2-4 (timeline, budget, description) ───────────────
  const questions = [
    {
      id: 'timeline',
      text: 'When do you need this verification completed?',
      placeholder: 'e.g., Within 3 days, Next week, Flexible...',
      examples: ['Urgent (within 3 days)', 'Within 1 week', 'Within 2 weeks', 'Within 1 month', 'Flexible']
    },
    {
      id: 'description',
      text: 'Any additional details for the verifier? (specific areas to inspect, standards to check against, reporting requirements, etc.)',
      placeholder: 'e.g., Please verify foundation work against approved structural drawings, check reinforcement placement, confirm concrete mix compliance, and provide a detailed photo report...',
      multiline: true,
      rows: 4,
      allowAI: true,
      allowFiles: true,
      optional: true
    }
  ];

  // currentQuestion is only valid for steps 2-4
  const currentQuestion = step >= 2 ? questions[step - 2] : null;
  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  // ── Fetch projects when the dialog opens ───────────────────────────────────
  useEffect(() => {
    if (!open) return;
    fetchProjects();
    if (verifierId) {
      setSelectedVerifiers([verifierId]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── When a project is selected, pre-fill scope description ───────────────
  useEffect(() => {
    if (!selectedProject) return;
    if (scopeType === 'entire') {
      setScopeDescription(buildVerificationScopeFromProject(selectedProject));
    }
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ────────────────────────────────────────────────────────────────
  const showSnackbarMsg = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const buildProjectContext = () => {
    if (!selectedProject) return '';
    return `Project context — Title: "${selectedProject.title}", Location: "${selectedProject.location}", Description: "${selectedProject.description}"`;
  };

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get('/projects');
      const list = response.data.data?.projects || response.data.data || [];
      setProjects(list);
      // Auto-associate when there is exactly one project
      if (list.length === 1) {
        setSelectedProject(list[0]);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  // ── Improve with AI ────────────────────────────────────────────────────────
  const handleImproveWithAI = async (text, setText) => {
    if (!text.trim()) {
      showSnackbarMsg('Please enter some content first', 'warning');
      return;
    }
    setImproving(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post('/bids/ai/improve-section', {
        section: step === 1 ? 'verification_scope' : 'verification_details',
        currentContent: text,
        improvementRequest: buildProjectContext()
      });
      setText(response.data.data.improvedContent);
      showSnackbarMsg('Content improved!', 'success');
    } catch (err) {
      console.error('Error improving content:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to improve content';
      showSnackbarMsg(msg, 'error');
    } finally {
      setImproving(false);
    }
  };

  // ── Scope type change ──────────────────────────────────────────────────────
  const handleScopeTypeChange = (newType) => {
    setScopeType(newType);
    setSelectedStageId('');
    setSelectedServiceId('');
    if (newType === 'entire' && selectedProject) {
      setScopeDescription(buildVerificationScopeFromProject(selectedProject));
    } else {
      setScopeDescription('');
    }
  };

  // ── Construction stage selected ────────────────────────────────────────────
  const handleStageChange = (stageId) => {
    setSelectedStageId(stageId);
    const stage = CONSTRUCTION_STAGES.find(s => s.id === stageId);
    setScopeDescription(stage ? stage.suggestion : '');
  };

  // ── Verification service selected ──────────────────────────────────────────
  const handleServiceChange = (serviceId) => {
    setSelectedServiceId(serviceId);
    const service = VERIFICATION_SERVICES.find(s => s.id === serviceId);
    setScopeDescription(service ? service.suggestion : '');
  };

  // ── File upload ────────────────────────────────────────────────────────────
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length + uploadedFiles.length > 10) {
      showSnackbarMsg('Maximum 10 files allowed', 'warning');
      return;
    }
    setUploadedFiles([...uploadedFiles, ...files]);
    showSnackbarMsg(`${files.length} file(s) added`, 'success');
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  // ── Verifier selection ─────────────────────────────────────────────────────
  const handleToggleVerifier = (vId) => {
    setSelectedVerifiers(prev =>
      prev.includes(vId)
        ? prev.filter(id => id !== vId)
        : [...prev, vId]
    );
  };

  const filteredVerifiers = matchedVerifiers.filter(verifier => {
    const name = (verifier.full_name || verifier.name || `${verifier.first_name || ''} ${verifier.last_name || ''}`).toLowerCase();
    const location = (verifier.city || verifier.location || '').toLowerCase();
    const tier = (verifier.tier || '').toLowerCase();
    const vId = verifier.id || verifier.user_id;

    const matchesSearch = !filterSearch || name.includes(filterSearch.toLowerCase());
    const matchesLocation = !filterLocation || location.includes(filterLocation.toLowerCase());
    const matchesTier = !filterTier || tier.includes(filterTier.toLowerCase());
    const matchesPrevious = !showPreviousOnly || previousVerifiers.includes(vId);

    return matchesSearch && matchesLocation && matchesTier && matchesPrevious;
  });

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleNext = async () => {
    setError(null);

    // ── Step 0: Project Association ──────────────────────────────────────────
    if (step === 0) {
      if (!selectedProject) {
        setError('Please select a project to continue.');
        return;
      }
      setAiTyping(true);
      await new Promise(r => setTimeout(r, 500));
      setAiTyping(false);
      setStep(1);
      return;
    }

    // ── Step 1: Scope Selection ───────────────────────────────────────────────
    if (step === 1) {
      if (!scopeDescription.trim()) {
        setError('Please describe the scope of verification to continue.');
        return;
      }
      if (scopeType === 'stage' && !selectedStageId) {
        setError('Please select a construction stage to continue.');
        return;
      }
      if (scopeType === 'service' && !selectedServiceId) {
        setError('Please select a verification service to continue.');
        return;
      }
      // Commit scope + location into answers so the backend payload is correct
      const newAnswers = {
        ...answers,
        workType: scopeDescription.trim(),
        location: selectedProject?.location || ''
      };
      setAnswers(newAnswers);
      setAiTyping(true);
      await new Promise(r => setTimeout(r, 500));
      setAiTyping(false);
      setStep(2);
      return;
    }

    // ── Steps 2-4: timeline / budget / description ────────────────────────────
    if (!currentAnswer.trim() && !currentQuestion.optional) {
      setError('Please provide an answer to continue.');
      return;
    }

    const newAnswers = {
      ...answers,
      [currentQuestion.id]: currentAnswer.trim()
    };
    setAnswers(newAnswers);
    setCurrentAnswer('');
    setError(null);

    setAiTyping(true);
    await new Promise(r => setTimeout(r, 800));
    setAiTyping(false);

    if (step < TOTAL_STEPS - 1) {
      setStep(step + 1);
    } else {
      // Last step completed
      if (verifierId) {
        // Pre-seeded verifier — skip verifier selection
        setSelectedVerifiers([verifierId]);
        setShowPreview(true);
      } else if (matchedVerifiers.length > 0) {
        setShowPreview(true);
      } else {
        await handleFetchVerifiers(newAnswers);
      }
    }
  };

  const handleBack = () => {
    if (step === 0) return;
    if (step <= 2) {
      setStep(step - 1);
      setCurrentAnswer('');
      return;
    }
    // Step 3: restore the previous question's answer into the text field
    const prevQuestion = questions[step - 3]; // step=3→questions[0]
    setStep(step - 1);
    setCurrentAnswer(answers[prevQuestion?.id] || '');
  };

  const handleExampleClick = (example) => {
    setCurrentAnswer(example);
  };

  // ── Fetch verifiers after all questions are answered ─────────────────────
  const handleFetchVerifiers = async (finalAnswers) => {
    setIsLoading(true);
    setError(null);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get('/verifiers');
      const verifiers = response.data.data || response.data || [];

      if (verifiers.length === 0) {
        setError('No verifiers available at the moment. Please try again later.');
        setIsLoading(false);
        return;
      }

      try {
        const historyResponse = await apiClient.get('/hires/verifier-history');
        const history = historyResponse.data.data || historyResponse.data || [];
        setPreviousVerifiers(history.map(v => v.verifier_id || v.id));
      } catch {
        setPreviousVerifiers([]);
      }

      setMatchedVerifiers(verifiers);
      setSelectedVerifiers([]);
      setShowPreview(true);
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading verifiers:', err);
      setError(err.response?.data?.message || 'Failed to load verifiers. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSendRequests = () => {
    setShowPreview(false);
    if (verifierId) {
      // Pre-seeded: go straight to submit, bypassing verifier selection UI
      handleSubmitDocument();
    } else {
      setShowVerifierSelection(true);
    }
  };

  const handleSubmitDocument = async () => {
    if (selectedVerifiers.length === 0) {
      showSnackbarMsg('Please select at least one verifier', 'warning');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const apiClient = getApiClient();
      const quoteRequests = selectedVerifiers.map(vId => {
        const verifier = matchedVerifiers.find(v => v.id === vId);
        return apiClient.post('/quote-requests', {
          verifierId: verifier?.user_id || vId,
          verifierName: verifier?.full_name || verifier?.name || `${verifier?.first_name || ''} ${verifier?.last_name || ''}`.trim() || undefined,
          targetType: 'verifier',
          projectId: selectedProject?.id || null,
          projectDescription: answers.description || answers.workType,
          projectLocation: answers.location || selectedProject?.location,
          timeline: answers.timeline || null
        });
      });

      await Promise.all(quoteRequests);

      setSuccess(true);
      showSnackbarMsg(`Quote requests sent to ${selectedVerifiers.length} verifier${selectedVerifiers.length > 1 ? 's' : ''}!`, 'success');

      setTimeout(() => {
        if (onComplete) {
          onComplete({
            verifiers: matchedVerifiers.filter(v => selectedVerifiers.includes(v.id)),
            answers
          });
        }
        handleClose();
      }, 3000);
    } catch (err) {
      console.error('Error sending quote requests:', err);
      setError(err.response?.data?.message || 'Failed to send requests. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset & close ──────────────────────────────────────────────────────────
  const handleClose = () => {
    if (isLoading) return;
    setStep(0);
    setAnswers({});
    setCurrentAnswer('');
    setError(null);
    setSuccess(false);
    setMatchedVerifiers([]);
    setSelectedVerifiers([]);
    setShowVerifierSelection(false);
    setShowPreview(false);
    setUploadedFiles([]);
    // project / scope state
    setProjects([]);
    setSelectedProject(null);
    setProjectsLoading(false);
    setScopeType('entire');
    setScopeDescription('');
    setSelectedStageId('');
    setSelectedServiceId('');
    onClose();
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, minHeight: '600px', maxHeight: '90vh' }
      }}
    >
      <DialogCloseButton onClick={handleClose} sx={{ color: 'white' }} />
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          background: brand.gradient,
          color: 'white',
          py: 3,
          px: 3
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <VerifiedUser sx={{ fontSize: 32 }} />
          <Box flexGrow={1}>
            <Typography variant="h5" fontWeight={700}>
              Request Verifier Quotes
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Describe your verification needs and receive quotes from qualified verifiers
            </Typography>
          </Box>
        </Box>

        {!showVerifierSelection && !success && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': { backgroundColor: 'white', borderRadius: 3 }
              }}
            />
            <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.9 }}>
              Step {step + 1} of {TOTAL_STEPS}
            </Typography>
          </Box>
        )}
        {showVerifierSelection && !success && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Select verifiers to send your quote request to
            </Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* ── Success ── */}
        {success && (
          <Fade in={success}>
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Quote request sent!</Typography>
              <Typography variant="body2">
                Your verification request has been sent to {selectedVerifiers.length} verifier{selectedVerifiers.length > 1 ? 's' : ''}.
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Expect to receive quotes within 24-48 hours.
              </Typography>
            </Alert>
          </Fade>
        )}

        {/* ── Error ── */}
        {error && !success && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* ════════════════════════════════════════════════════════════
            Verifier Selection Grid (post-questions)
        ════════════════════════════════════════════════════════════ */}
        {showVerifierSelection && !success && (
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Choose Who Receives Your Quote Request
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the verifiers you'd like to send your quote request to.
            </Typography>

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth size="small" placeholder="Search by name..."
                    value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)}
                    InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>🔍</Box> }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth size="small" placeholder="Location..."
                    value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)}
                    InputProps={{ startAdornment: <LocationOn sx={{ mr: 0.5, color: 'text.secondary', fontSize: 20 }} /> }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth size="small" placeholder="Tier (e.g. Professional)..."
                    value={filterTier} onChange={(e) => setFilterTier(e.target.value)}
                    InputProps={{ startAdornment: <Shield sx={{ mr: 0.5, color: 'text.secondary', fontSize: 20 }} /> }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPreviousOnly}
                        onChange={(e) => setShowPreviousOnly(e.target.checked)}
                        size="small" color="success"
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>Worked with before</Typography>}
                    sx={{ ml: 0.5 }}
                  />
                </Grid>
              </Grid>
              {showPreviousOnly && previousVerifiers.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  You haven't worked with any verifiers yet
                </Typography>
              )}
            </Paper>

            <Paper elevation={0} sx={{ backgroundColor: '#ede7f6', border: `1px solid ${brand.primary}`, borderRadius: 2, p: 2, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" fontWeight={600} color={brand.primary}>
                  {selectedVerifiers.length} verifier{selectedVerifiers.length !== 1 ? 's' : ''} selected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredVerifiers.length} of {matchedVerifiers.length}
                </Typography>
              </Box>
            </Paper>

            <Grid container spacing={2}>
              {filteredVerifiers.map((verifier) => (
                <Grid item xs={12} sm={6} key={verifier.id}>
                  <Card
                    sx={{
                      border: selectedVerifiers.includes(verifier.id) ? `2px solid ${brand.primary}` : `1px solid ${border.default}`,
                      borderRadius: 2, cursor: 'pointer', transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': { boxShadow: 3, borderColor: brand.primary }
                    }}
                    onClick={() => handleToggleVerifier(verifier.id)}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        <Checkbox
                          checked={selectedVerifiers.includes(verifier.id)}
                          onChange={() => handleToggleVerifier(verifier.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ mt: -1, ml: -1 }}
                        />
                        <Avatar
                          src={verifier.profile_image_url || verifier.avatar}
                          alt={verifier.name || verifier.full_name || `${verifier.first_name || ''} ${verifier.last_name || ''}`}
                          sx={{ width: 48, height: 48, bgcolor: brand.primary }}
                        >
                          {(verifier.first_name || verifier.name || verifier.full_name)?.charAt(0)}
                        </Avatar>
                        <Box flexGrow={1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {verifier.full_name || verifier.name || `${verifier.first_name || ''} ${verifier.last_name || ''}`}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
                            {verifier.tier && (
                              <Chip label={verifier.tier.toUpperCase()} size="small"
                                sx={{ backgroundColor: brand.primary, color: 'white', fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
                            )}
                            {previousVerifiers.includes(verifier.id || verifier.user_id) && (
                              <Chip label="Worked With" size="small"
                                sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem', height: 18, fontWeight: 600 }} />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                            {(verifier.average_rating > 0 || verifier.rating > 0) && (
                              <Box display="flex" alignItems="center">
                                <Star sx={{ fontSize: 16, color: '#ffc107', mr: 0.3 }} />
                                <Typography variant="body2" color="text.secondary">
                                  {parseFloat(verifier.average_rating || verifier.rating).toFixed(1)}
                                </Typography>
                              </Box>
                            )}
                            {(verifier.city || verifier.location) && (
                              <Box display="flex" alignItems="center">
                                <LocationOn sx={{ fontSize: 16, color: '#666', mr: 0.3 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {verifier.city || verifier.location}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════════
            Questions (steps 0-4)
        ════════════════════════════════════════════════════════════ */}
        {!success && !showVerifierSelection && (
          <>
            {/* ── Step 0: Project Association ─────────────────────────────── */}
            {step === 0 && (
              <Paper
                elevation={0}
                sx={{ backgroundColor: '#f5f5f5', boxShadow: `inset 0 0 0 2px ${brand.primary}`, borderRadius: 2, p: 3, mb: 3, position: 'relative' }}
              >
                {/* AI bubble */}
                <Box sx={{
                  position: 'absolute', top: -20, left: 20, zIndex: 1,
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: brand.primary, border: '3px solid white', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', boxShadow: 3
                }}>
                  <FolderOpen sx={{ color: 'white', fontSize: 22 }} />
                </Box>

                <Typography variant="h6" sx={{ mt: 1, mb: 2, color: brand.primaryDark }}>
                  Which project is this verification for?
                </Typography>

                {projectsLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading your projects...</Typography>
                  </Box>
                )}

                {!projectsLoading && projects.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      You don't have any projects yet.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => { handleClose(); window.location.href = '/projects/new'; }}
                    >
                      Create your first project
                    </Button>
                  </Alert>
                )}

                {!projectsLoading && projects.length > 1 && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select project</InputLabel>
                    <Select
                      value={selectedProject?.id || ''}
                      onChange={(e) => {
                        const p = projects.find(pr => pr.id === e.target.value);
                        setSelectedProject(p || null);
                        setScopeType('entire');
                        setScopeDescription(buildVerificationScopeFromProject(p));
                        setSelectedStageId('');
                        setSelectedServiceId('');
                      }}
                      label="Select project"
                    >
                      {projects.map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Project confirmation card */}
                {selectedProject && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2, mt: 1,
                      backgroundColor: '#ede7f6',
                      border: '1px solid #b39ddb',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      {selectedProject.title}
                    </Typography>
                    {selectedProject.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedProject.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: brand.primary }} />
                      <Typography variant="body2" color={brand.primary} fontWeight={500}>
                        {selectedProject.location}
                      </Typography>
                    </Box>
                    {projects.length === 1 && (
                      <Chip label="Auto-associated" size="small" sx={{ mt: 1, bgcolor: '#d1c4e9', color: '#4527a0', fontSize: '0.7rem' }} />
                    )}
                  </Paper>
                )}
              </Paper>
            )}

            {/* ── Step 1: Scope Selection ──────────────────────────────────── */}
            {step === 1 && (
              <Paper
                elevation={0}
                sx={{ backgroundColor: '#f5f5f5', boxShadow: `inset 0 0 0 2px ${brand.primary}`, borderRadius: 2, p: 3, mb: 3, position: 'relative' }}
              >
                <Box sx={{
                  position: 'absolute', top: -20, left: 20, zIndex: 1,
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: brand.primary, border: '3px solid white', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', boxShadow: 3
                }}>
                  <Assignment sx={{ color: 'white', fontSize: 22 }} />
                </Box>

                <Typography variant="h6" sx={{ mt: 1, mb: 2, color: brand.primaryDark }}>
                  What do you need verified?
                </Typography>

                <RadioGroup value={scopeType} onChange={(e) => handleScopeTypeChange(e.target.value)}>
                  <FormControlLabel value="entire" control={<Radio />} label="Entire Project" />
                  <FormControlLabel value="stage" control={<Radio />} label="A Construction Stage" />
                  <FormControlLabel value="service" control={<Radio />} label="A Verification Service" />
                </RadioGroup>

                {/* Construction stage dropdown */}
                {scopeType === 'stage' && (
                  <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                    <InputLabel>Select construction stage</InputLabel>
                    <Select
                      value={selectedStageId}
                      onChange={(e) => handleStageChange(e.target.value)}
                      label="Select construction stage"
                    >
                      {CONSTRUCTION_STAGES.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {s.purpose}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Verification service dropdown */}
                {scopeType === 'service' && (
                  <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                    <InputLabel>Select verification service</InputLabel>
                    <Select
                      value={selectedServiceId}
                      onChange={(e) => handleServiceChange(e.target.value)}
                      label="Select verification service"
                    >
                      <ListSubheader sx={{ fontWeight: 700, color: brand.primaryDark }}>Professional Verifier Services</ListSubheader>
                      {VERIFICATION_SERVICES.filter(s => s.category === 'Professional').map(s => (
                        <MenuItem key={s.id} value={s.id}>
                          <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                        </MenuItem>
                      ))}
                      <ListSubheader sx={{ fontWeight: 700, color: '#2e7d32' }}>General Verifier Services</ListSubheader>
                      {VERIFICATION_SERVICES.filter(s => s.category === 'General').map(s => (
                        <MenuItem key={s.id} value={s.id}>
                          <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                        </MenuItem>
                      ))}
                      <Divider />
                      <MenuItem value="other">
                        <Typography variant="body2" fontWeight={500} color="primary">Other — Write your own</Typography>
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* Description textarea — shown when scope is clear */}
                {(scopeType === 'entire' || (scopeType === 'stage' && selectedStageId) || (scopeType === 'service' && selectedServiceId)) && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={scopeDescription}
                      onChange={(e) => setScopeDescription(e.target.value)}
                      disabled={improving}
                      placeholder={
                        scopeType === 'entire'
                          ? 'Describe what the verifier should inspect and verify across the full project (auto-filled, edit as needed)...'
                          : scopeType === 'stage'
                          ? 'Describe what this stage verification requires — what should the verifier inspect and check?'
                          : selectedServiceId === 'other'
                          ? 'Describe your custom verification need — what should the verifier inspect, assess, or certify?'
                          : 'Describe the verification service requirements — what should the verifier check and report on?'
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          fontSize: '16px',
                          lineHeight: 1.6
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        size="small"
                        startIcon={improving ? <CircularProgress size={16} /> : <AutoAwesome />}
                        onClick={() => handleImproveWithAI(scopeDescription, setScopeDescription)}
                        disabled={!scopeDescription.trim() || improving}
                      >
                        {improving ? 'Improving...' : 'Improve with AI'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {/* ── Steps 2-3: timeline / description ───────────────── */}
            {step >= 2 && currentQuestion && (
              <Paper
                elevation={0}
                sx={{ backgroundColor: '#f5f5f5', boxShadow: `inset 0 0 0 2px ${brand.primary}`, borderRadius: 2, p: 3, mb: 3, position: 'relative' }}
              >
                <Box sx={{
                  position: 'absolute', top: -20, left: 20, zIndex: 1,
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: brand.primary, border: '3px solid white', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', boxShadow: 3
                }}>
                  <AutoAwesome sx={{ color: 'white', fontSize: 24 }} />
                </Box>

                <Typography variant="h6" sx={{ mt: 1, mb: 2, color: brand.primaryDark }}>
                  {currentQuestion.text}
                </Typography>

                {currentQuestion.examples && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Quick select:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {currentQuestion.examples.map((example, idx) => (
                        <Chip
                          key={idx} label={example}
                          onClick={() => handleExampleClick(example)}
                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#ede7f6', borderColor: brand.primary } }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <TextField
                  fullWidth
                  multiline
                  minRows={currentQuestion.rows || 2}
                  maxRows={6}
                  value={currentAnswer}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  placeholder={currentQuestion.placeholder}
                  disabled={isLoading || aiTyping || improving}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !currentQuestion.multiline && !isLoading) {
                      e.preventDefault();
                      handleNext();
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      fontSize: '16px',
                      lineHeight: 1.6
                    }
                  }}
                />

                {/* Improve with AI — description step */}
                {currentQuestion.allowAI && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      size="small"
                      startIcon={improving ? <CircularProgress size={16} /> : <AutoAwesome />}
                      onClick={() => handleImproveWithAI(currentAnswer, setCurrentAnswer)}
                      disabled={!currentAnswer.trim() || improving || isLoading}
                    >
                      {improving ? 'Improving...' : 'Improve with AI'}
                    </Button>
                  </Box>
                )}

                {/* File upload — description step */}
                {currentQuestion.allowFiles && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFile />}
                      size="small"
                      disabled={isLoading}
                    >
                      Upload Photos
                      <input type="file" hidden multiple accept="image/*" onChange={handleFileUpload} />
                    </Button>
                    {uploadedFiles.length > 0 && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        {uploadedFiles.map((file, index) => (
                          <Chip
                            key={index}
                            icon={<Image />}
                            label={file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}
                            onDelete={() => handleRemoveFile(index)}
                            size="small"
                            sx={{ maxWidth: 200 }}
                          />
                        ))}
                      </Stack>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {uploadedFiles.length}/10 files uploaded
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}

            {/* AI typing indicator */}
            {aiTyping && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Processing...</Typography>
              </Box>
            )}

            {/* Previous answers summary */}
            {step > 0 && Object.keys(answers).length > 0 && (
              <Paper elevation={0} sx={{ backgroundColor: bg.subtle, border: `1px solid ${border.default}`, borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Your Answers:
                </Typography>
                {Object.entries(answers).map(([key, value]) => (
                  <Typography key={key} variant="body2" color="text.secondary">
                    <strong>{ANSWER_LABELS[key] || key}:</strong> {value}
                  </Typography>
                ))}
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      {/* ── Dialog Actions ── */}
      {!success && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${border.default}` }}>
          {showVerifierSelection ? (
            <>
              <Button
                onClick={() => { setShowVerifierSelection(false); setShowPreview(true); }}
                disabled={isLoading}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back to Preview
              </Button>
              <Box flexGrow={1} />
              <Button onClick={handleClose} disabled={isLoading} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSubmitDocument}
                disabled={isLoading || selectedVerifiers.length === 0}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{
                  background: brand.gradient,
                  color: 'white', textTransform: 'none', fontWeight: 600, px: 3,
                  '&:hover': { background: brand.gradientHover }
                }}
              >
                Send Quote Requests ({selectedVerifiers.length})
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleBack}
                disabled={step === 0 || isLoading}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
              <Box flexGrow={1} />
              <Button onClick={handleClose} disabled={isLoading} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  isLoading || aiTyping ||
                  (step === 0 && (projectsLoading || projects.length === 0 || !selectedProject)) ||
                  (step === 1 && (!scopeDescription.trim() || (scopeType === 'stage' && !selectedStageId) || (scopeType === 'service' && !selectedServiceId))) ||
                  (step >= 2 && !currentAnswer.trim() && !currentQuestion?.optional)
                }
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{
                  background: brand.gradient,
                  color: 'white', textTransform: 'none', fontWeight: 600, px: 3,
                  '&:hover': { background: brand.gradientHover }
                }}
              >
                {step === TOTAL_STEPS - 1 ? 'Preview & Send' : 'Next'}
              </Button>
            </>
          )}
        </DialogActions>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Document Preview Modal ── */}
      {showPreview && (
        <VerifierHireDocumentPreview
          open={showPreview}
          onClose={() => {
            setShowPreview(false);
            setStep(TOTAL_STEPS - 1);
            setCurrentAnswer(answers[questions[questions.length - 1].id] || '');
          }}
          onSubmit={handleSendRequests}
          data={{
            projectTitle: selectedProject ? selectedProject.title : (answers.workType || 'Verification Project'),
            contractorName: user?.company_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Client',
            verificationType: answers.workType,
            location: answers.location || selectedProject?.location,
            projectSummary: answers.description,
            areasToVerify: answers.description,
            focusPoints: answers.description,
            complianceStandards: 'All relevant building codes and safety regulations',
            buildingCodes: 'Local and national building codes applicable to this project type',
            projectStage: 'In Progress',
            verificationPurpose: 'Quality assurance and compliance verification',
            stakeholders: 'Project owner, contractor, and regulatory authorities',
            startDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
            duration: answers.timeline,
            reportDeadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
            timelineUrgency: answers.timeline?.toLowerCase().includes('urgent') ? 'Urgent' : 'Standard',
            additionalDeliverables: 'Additional documentation as requested',
            documentsProvided: uploadedFiles.length > 0 ? `${uploadedFiles.length} file(s) attached` : 'Project plans and specifications will be provided',
            siteAccess: 'Site access will be coordinated with the verifier',
            additionalRequirements: 'Must have relevant experience in project type',
            numberOfVerifiers: selectedVerifiers.length,
            evaluationCriteria: '1. Qualifications and certifications\n2. Experience with similar projects\n3. Availability and timeline\n4. Fee structure and value\n5. References and reputation',
            termsAndConditions: `By submitting a proposal, verifiers acknowledge and agree to:\n\n\u2022 All information provided is accurate and complete\n\u2022 They have the necessary licenses, certifications, and insurance to perform the verification\n\u2022 They will maintain confidentiality of all project information\n\u2022 The client reserves the right to accept or reject any proposal\n\u2022 All verification work will be conducted professionally and ethically\n\u2022 Reports will comply with industry standards and be legally defensible\n\u2022 Verifier carries appropriate professional indemnity insurance`,
            responseDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            verifierTier: 'professional'
          }}
        />
      )}
    </Dialog>
  );
};

export default AIVerifierQuoteDialog;
